/**
 * FieldPickerPopover — search-first, entity-grouped field selector.
 *
 * Opens as a popover anchored to the trigger element. Fields are grouped
 * by semantic entity (source_entity), with optional secondary grouping
 * by role (Measures / Dimensions / Dates) when an entity has mixed types.
 *
 * Selecting a field immediately closes the popover and fires onSelect.
 */
import { useCallback, useMemo, useRef, useState } from "react";
import {
  Input,
  Popover,
  PopoverSurface,
  PopoverTrigger,
  makeStyles,
  tokens,
} from "@fluentui/react-components";
import { SearchRegular } from "@fluentui/react-icons";
import type { DatasetViewColumn } from "../../../../api/types";
import EntityGroup from "./EntityGroup";
import { useEditorDisplayNames } from '../../EditorDisplayNamesContext';
import { filterEntityFields, groupByEntity } from './entityFields';

// ─── Styles ──────────────────────────────────────────────────────────────────

const useStyles = makeStyles({
  surface: {
    padding: "0",
    width: "360px",
    maxHeight: "400px",
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
  },
  searchBox: {
    padding: "8px 10px",
    borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
  },
  list: {
    flex: 1,
    overflowY: "auto",
    padding: "0",
  },
  empty: {
    padding: "16px",
    textAlign: "center",
    color: tokens.colorNeutralForeground4,
    fontSize: tokens.fontSizeBase200,
  },
});

// ─── Props ───────────────────────────────────────────────────────────────────

export interface FieldPickerPopoverProps {
  columns: DatasetViewColumn[];
  /** Currently selected column name (for highlight) */
  value?: string;
  /** Called when a field is selected */
  onSelect: (columnName: string) => void;
  /** The trigger element (button/chip that opens the picker) */
  children: React.ReactElement;
  /** Filter columns to specific semantic types */
  filterTypes?: string[];
}

export default function FieldPickerPopover({
  columns,
  value,
  onSelect,
  children,
  filterTypes,
}: FieldPickerPopoverProps) {
  const styles = useStyles();
  const labels = useEditorDisplayNames();
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const searchRef = useRef<HTMLInputElement>(null);

  // Filter columns by allowed types
  const filteredByType = useMemo(() => {
    if (!filterTypes || filterTypes.length === 0) return columns;
    return columns.filter((c) => filterTypes.includes(c.semantic_type));
  }, [columns, filterTypes]);

  // Filter by search query
  const filtered = useMemo(() => filterEntityFields(filteredByType, search, labels), [filteredByType, search, labels]);

  // Group by entity
  const groups = useMemo(() => groupByEntity(filtered, labels), [filtered, labels]);

  // Has entity grouping data?
  const hasEntityData = columns.some((c) => c.source_entity);

  const handleSelect = useCallback(
    (colName: string) => {
      onSelect(colName);
      setOpen(false);
      setSearch("");
    },
    [onSelect],
  );

  const handleOpenChange = useCallback(
    (_: unknown, data: { open: boolean }) => {
      setOpen(data.open);
      if (data.open) {
        setSearch("");
        // Focus search on next tick
        setTimeout(() => searchRef.current?.focus(), 50);
      }
    },
    [],
  );

  return (
    <Popover
      open={open}
      onOpenChange={handleOpenChange}
      positioning="below-start"
      trapFocus
    >
      <PopoverTrigger disableButtonEnhancement>
        {children}
      </PopoverTrigger>
      <PopoverSurface className={styles.surface}>
        <div className={styles.searchBox}>
          <Input
            ref={searchRef}
            size="small"
            placeholder="Search fields..."
            contentBefore={<SearchRegular />}
            value={search}
            onChange={(_, data) => setSearch(data.value)}
            appearance="underline"
          />
        </div>
        <div className={styles.list} role="listbox" aria-label="Field picker">
          {groups.length === 0 ? (
            <div className={styles.empty}>No fields found</div>
          ) : hasEntityData ? (
            groups.map((g) => (
              <EntityGroup
                key={g.entity}
                label={g.label}
                fields={g.fields}
                selectedValue={value}
                onSelect={handleSelect}
                hideHeader={groups.length === 1}
              />
            ))
          ) : (
            // Flat list fallback when no source_entity data
            <EntityGroup
              label=""
              fields={filtered}
              selectedValue={value}
              onSelect={handleSelect}
              hideHeader
            />
          )}
        </div>
      </PopoverSurface>
    </Popover>
  );
}
